import type { ChangeEvent } from "react";
import { useState } from "react";

interface FormRecipientsProps {
	className?: string;
	style?: React.CSSProperties;
	width?: string;
	"data-testid"?: string;
	observationType?: IObservationType;

	heading?: string;

	defaultValue?: EmailRecipient[];

	recipients?: EmailRecipient[];

	specificEmailAddress?: string;

	checkboxErrorMessage?: string;

	emailRecipientErrorMessage?: string;

	isEmailRecipientInvalid?: boolean;

	isCheckboxGroupInvalid?: boolean;

	onCheckboxGroupChange?: (values: (string | number)[]) => void;

	onSpecificEmailAddressChange?: (value: string) => void;
}

const FormRecipients = ({
	observationType,
	heading,
	defaultValue,
	recipients,
	specificEmailAddress = "",
	isCheckboxGroupInvalid = false,
	isEmailRecipientInvalid = false,
	checkboxErrorMessage,
	emailRecipientErrorMessage,
	onCheckboxGroupChange,
	onSpecificEmailAddressChange,
	width,
	style,
	className,
	"data-testid": testId
}: FormRecipientsProps) => {
	// Internal state for uncontrolled usage (when recipients is not provided by parent)
	const [internalChecked, setInternalChecked] = useState<string[]>(
		(defaultValue ?? []) as string[]
	);

	const handleSpecificEmailAddressChange = (
		e: ChangeEvent<HTMLInputElement>
	) => {
		const { value } = e.target;

		onSpecificEmailAddressChange?.(value);
	};

	const checkboxOptions = [
		{ label: "Head of Region", value: "regional-manager" },
		{ label: "Area Manager", value: "area-manager" },
		{ label: "Store Manager", value: "store-manager" },
		{ label: "The individual being observed", value: "observed-individual" },
		{
			label: "The individual doing the observation",
			value: "observer"
		},
		{ label: "Send to a specific email address", value: "specific-email" }
	];

	const filteredCheckboxes = checkboxOptions.filter((option) => {
		const isStoreForm =
			observationType === "STORE_SCORED_ASSESSMENT" ||
			observationType === "STORE_SELF_ASSESSMENT";

		if (isStoreForm && option.value === "observed-individual") return;

		return option;
	});

	const handleCheckboxChange = (value: string, checked: boolean) => {
		const isControlled = recipients !== undefined;

		if (isControlled) {
			const current = (recipients ?? []) as string[];
			const updated: string[] = checked
				? [...current, value]
				: current.filter((v) => v !== value);
			onCheckboxGroupChange?.(updated);
		} else {
			const updated: string[] = checked
				? [...internalChecked, value]
				: internalChecked.filter((v) => v !== value);
			setInternalChecked(updated);
			onCheckboxGroupChange?.(updated);
		}
	};

	// Use controlled recipients if provided, otherwise use internal state
	const currentRecipients: string[] =
		recipients !== undefined ? (recipients as string[]) : internalChecked;

	return (
		<div className={className} style={{ width, ...style }} data-testid={testId}>
			{heading && (
				<h2 className="text-left mb-2 text-xl font-semibold">{heading}</h2>
			)}

			<div className={isCheckboxGroupInvalid ? "has-error" : ""}>
				<div className="flex flex-col items-start gap-1">
					{filteredCheckboxes.map((checkbox) => (
						<label
							key={checkbox.value}
							className="flex items-center gap-2 cursor-pointer"
						>
							<input
								type="checkbox"
								value={checkbox.value}
								checked={currentRecipients.includes(checkbox.value)}
								onChange={(e) =>
									handleCheckboxChange(checkbox.value, e.target.checked)
								}
							/>
							{checkbox.label}
						</label>
					))}
				</div>

				{isCheckboxGroupInvalid && checkboxErrorMessage && (
					<p className="text-destructive text-sm mt-1">
						{checkboxErrorMessage}
					</p>
				)}
			</div>

			{(currentRecipients.includes("specific-email") ||
				defaultValue?.includes("specific-email")) && (
				<div
					className={`mt-2 ml-6 ${isEmailRecipientInvalid ? "has-error" : ""}`}
				>
					<input
						className="w-[90%] rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
						placeholder="Enter email address"
						aria-label="Enter email address"
						value={specificEmailAddress}
						onChange={handleSpecificEmailAddressChange}
					/>

					{isEmailRecipientInvalid && emailRecipientErrorMessage && (
						<p className="text-destructive text-sm mt-1">
							{emailRecipientErrorMessage}
						</p>
					)}
				</div>
			)}
		</div>
	);
};

export { FormRecipients, type FormRecipientsProps };
